/**
 * FP&A: comparações entre versões, Realizado × Projetado e painel (ADR 0032, seção 11). Cálculos
 * em decimal exato. Mês sem valor é "sem dados", nunca zero: a diferença só existe quando os dois
 * lados têm valor, e o percentual só quando a base é diferente de zero.
 */
import { z } from 'zod';
import { costCenterScopeFields } from './cost-center.ts';
import { fromScaled, toScaled } from './money.ts';
import { FPA_PRESENTATION_CURRENCIES } from './fpa-dre.ts';

/** Percentual com 2 casas em fração exata, para o formato de percentual do Excel (5.50 → 0.0550). */
export function percentFraction(pct: string): string {
  const neg = pct.startsWith('-');
  const [int = '0', frac = ''] = (neg ? pct.slice(1) : pct).split('.');
  const digits = `${int}${frac.padEnd(2, '0')}`.replace(/^0+/, '') || '0';
  const padded = digits.padStart(5, '0');
  const value = `${padded.slice(0, -4)}.${padded.slice(-4)}`;
  return neg && /[1-9]/.test(value) ? `-${value}` : value;
}

export const FPA_ANALYSIS_DIMENSIONS = [
  'total',
  'head',
  'cost_center',
  'entity',
  'classification',
  'expense_type',
] as const;
export type FpaAnalysisDimension = (typeof FPA_ANALYSIS_DIMENSIONS)[number];

const uuid = z.uuid();
const filters = {
  legalEntityId: uuid.optional(),
  costCenterId: uuid.optional(),
  // Recorte por centros (ADR 0047): "Meus centros", por centro Master ou por centro Gerencial.
  ...costCenterScopeFields,
};

export const fpaCompareQuery = z.object({
  a: uuid,
  b: uuid,
  by: z.enum(FPA_ANALYSIS_DIMENSIONS).default('total'),
  /** Moeda de apresentação; sem ela, cada moeda numa linha, sem converter. */
  currency: z.enum(FPA_PRESENTATION_CURRENCIES).optional(),
  ...filters,
});
export type FpaCompareQuery = z.infer<typeof fpaCompareQuery>;

export const fpaAnalysisQuery = z.object({
  by: z.enum(FPA_ANALYSIS_DIMENSIONS).default('total'),
  ...filters,
});

export const fpaDashboardQuery = z.object({
  forecastId: uuid.optional(),
  budgetId: uuid.optional(),
  referenceId: uuid.optional(),
});

/** Títulos das colunas da planilha, já traduzidos pela tela. */
const label = z.string().trim().min(1).max(80);
export const fpaExportInput = z.object({
  by: z.enum(FPA_ANALYSIS_DIMENSIONS).default('total'),
  ...filters,
  labels: z.object({
    sheetLines: label,
    sheetTotals: label,
    group: label,
    entity: label,
    // Centro de custo em três colunas: código, nome e "código - nome" (costCenter).
    costCenterCode: label,
    costCenterName: label,
    costCenter: label,
    head: label,
    classification: label,
    expenseType: label,
    currency: label,
    total: label,
    a: label.optional(),
    b: label.optional(),
    diff: label.optional(),
    pct: label.optional(),
    months: z.array(label).length(12),
    // Aviso da visão sem os valores de pessoal (ADR 0049, R14), já traduzido pela tela.
    partial: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .default('Visão parcial — valores de pessoal não incluídos'),
    // Rótulo das somas de quem não vê pessoal: nunca "Total" (sigilo v3.1).
    visibleTotal: z.string().trim().min(1).max(60).default('Total visível'),
    // Moeda de apresentação, critério de câmbio e pendências de conversão, já traduzidos.
    conversion: z.string().trim().min(1).max(600).optional(),
  }),
});
export type FpaExportInput = z.infer<typeof fpaExportInput>;

export const fpaCompareExportInput = fpaExportInput.extend({
  a: uuid,
  b: uuid,
  currency: z.enum(FPA_PRESENTATION_CURRENCIES).optional(),
});

/** Totais de um grupo (dimensão e moeda), por mês (AAAA-MM-01). */
export interface FpaGroup {
  label: string | null;
  currency: string;
  months: Record<string, string>;
}

export interface FpaCell {
  a: string | null;
  b: string | null;
  diff: string | null;
  pct: string | null;
}

export interface FpaCompareRow {
  label: string | null;
  currency: string;
  /** Mês do ano ("01" a "12"): alinha exercícios diferentes (2026 × 2027). */
  months: Record<string, FpaCell>;
  total: FpaCell;
  /**
   * Soma só do que a pessoa vê, num grupo que pode ter pessoal oculto (sigilo v3.1): os valores
   * são "Total visível" e a variação não é calculada, para não passar por resultado completo.
   */
  visibleOnly?: boolean;
}

/** Linha de comparação restrita: mantém os valores visíveis e tira diferença e percentual. */
export function restrictCompareRow(row: FpaCompareRow): FpaCompareRow {
  const strip = (c: FpaCell): FpaCell => ({ ...c, diff: null, pct: null });
  return {
    ...row,
    months: Object.fromEntries(Object.entries(row.months).map(([m, c]) => [m, strip(c)])),
    total: strip(row.total),
    visibleOnly: true,
  };
}

/** Os meses do ano, "01" a "12". */
export const MONTH_KEYS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

const sumOrNull = (values: (string | null | undefined)[]): string | null => {
  const present = values.filter((v): v is string => v != null);
  return present.length ? fromScaled(present.reduce((s, v) => s + toScaled(v), 0n)) : null;
};

/** Diferença e percentual entre a base (a) e a comparada (b). */
export function compareCell(a: string | null, b: string | null): FpaCell {
  if (a === null || b === null) return { a, b, diff: null, pct: null };
  const sa = toScaled(a);
  const diff = toScaled(b) - sa;
  if (sa === 0n) return { a, b, diff: fromScaled(diff), pct: null };
  // Percentual com 2 casas, arredondado para longe do zero.
  const abs = sa < 0n ? -sa : sa;
  const n = diff * 10000n;
  const q = n / abs;
  const r = n % abs;
  const up = (r < 0n ? -r : r) * 2n >= abs;
  const pct = up ? q + (n < 0n ? -1n : 1n) : q;
  const neg = pct < 0n;
  const p = (neg ? -pct : pct).toString().padStart(3, '0');
  return { a, b, diff: fromScaled(diff), pct: `${neg ? '-' : ''}${p.slice(0, -2)}.${p.slice(-2)}` };
}

/** Totais por mês do ano de um grupo. */
function byMonthOfYear(months: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [month, value] of Object.entries(months)) out[month.slice(5, 7)] = value;
  return out;
}

/**
 * Compara duas versões grupo a grupo, mês a mês (pelo mês do ano), na mesma moeda. Grupo que só
 * existe de um lado aparece com o outro lado "sem dados".
 */
export function compareGroups(a: readonly FpaGroup[], b: readonly FpaGroup[]): FpaCompareRow[] {
  const keys = new Map<string, { label: string | null; currency: string }>();
  const index = (list: readonly FpaGroup[]) => {
    const m = new Map<string, Record<string, string>>();
    for (const g of list) {
      const k = `${g.label ?? ''}|${g.currency}`;
      keys.set(k, { label: g.label, currency: g.currency });
      m.set(k, byMonthOfYear(g.months));
    }
    return m;
  };
  const ia = index(a);
  const ib = index(b);
  return [...keys.entries()]
    .map(([k, { label, currency }]) => {
      const ma = ia.get(k) ?? {};
      const mb = ib.get(k) ?? {};
      const months = Object.fromEntries(
        MONTH_KEYS.map((m) => [m, compareCell(ma[m] ?? null, mb[m] ?? null)]),
      );
      const total = compareCell(sumOrNull(Object.values(ma)), sumOrNull(Object.values(mb)));
      return { label, currency, months, total };
    })
    .sort(
      (x, y) =>
        x.currency.localeCompare(y.currency) || (x.label ?? '￿').localeCompare(y.label ?? '￿'),
    );
}

/** Total do ano de um grupo (null = sem dados). */
export function groupTotal(g: FpaGroup): string | null {
  return sumOrNull(Object.values(g.months));
}

/** Os maiores grupos pelo valor absoluto do total do ano, numa moeda. */
export function topGroups(groups: readonly FpaGroup[], currency: string, limit = 10) {
  return groups
    .filter((g) => g.currency === currency)
    .map((g) => ({ label: g.label, total: groupTotal(g) }))
    .filter((g): g is { label: string | null; total: string } => g.total !== null)
    .sort((x, y) => {
      const ax = toScaled(x.total);
      const ay = toScaled(y.total);
      const abs = (v: bigint) => (v < 0n ? -v : v);
      return abs(ay) > abs(ax) ? 1 : abs(ay) < abs(ax) ? -1 : 0;
    })
    .slice(0, limit);
}

/** Soma por situação do mês (realizado fechado, parcial, projetado, sem situação), por moeda. */
export function totalsByState(
  groups: readonly FpaGroup[],
  states: Record<string, string>,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, bigint>> = {};
  for (const g of groups) {
    for (const [month, value] of Object.entries(g.months)) {
      const state = states[month] ?? 'unspecified';
      out[state] ??= {};
      out[state][g.currency] = (out[state][g.currency] ?? 0n) + toScaled(value);
    }
  }
  return Object.fromEntries(
    Object.entries(out).map(([s, c]) => [
      s,
      Object.fromEntries(Object.entries(c).map(([cur, v]) => [cur, fromScaled(v)])),
    ]),
  );
}
